import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CompletedRedirectPanel } from '@/components/shared/CompletedRedirectPanel';
import { CheckIcon, Loader2, ShieldCheck, ExternalLink, ArrowLeft, RefreshCw, Info } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { BankAccountSummary } from '@/types/accounts';
import KycModal from '@/components/kyc/KycModal';
import { useArrivalTimeout } from '@/hooks/useArrivalTimeout';
import { captureError } from '@/lib/observability';
import { isTerminalConsentFailure } from '@/lib/consentStatus';
import { renewalIdentity, renewalRefusedError } from '@/lib/renewalRequest';
import { useTranslations } from 'use-intl';
import { userFacingErrorMessage } from '@/utils/userFacingError';
import type { AccountType } from '@/types/accounts';

// Issue #386 audit: this dialog's own startConsentPolling had NO ceiling at all — not even an
// error-count breaker like useConsentPolling/usePaymentPolling have. A poll that always resolves
// 200 with isCompleted still false (or that always throws, silently "continuing" per its catch
// block's comment) spun forever, showing "Waiting for authorization" with no indication anything
// was wrong beyond the manual "Back" button. Same 3-minute ceiling as useConsentPolling — this is
// the same BankID/QR consent-authorization flow, just re-implemented locally for the refresh case.
const ARRIVAL_TIMEOUT_MS = 3 * 60 * 1000;

/** What the global renewal dialog is opened with (see src/lib/renewalRequest.ts). */
export interface ConsentRenewalTarget {
  accountId: string;
  accountIds?: string[];
  bankName?: string;
  bankBic?: string;
  /** A bic-less bank's identity (FIN-885) -- renewal is keyed on these when there is no bic. */
  bankRef?: string;
  bankCountryCode?: string;
  accountLabels?: string[];
  /** FIN-1567: the stored type of the account(s) being renewed -- always the account's own
   *  existing type (EC7: pre-FIN-1567 rows are undefined, which the server reads as business),
   *  never asked again here (AC7: renewal reuses the login without showing the cards). */
  accountType?: AccountType;
}

interface ConsentRefreshDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (accounts: BankAccountSummary[]) => void;
  accountId: string; // Keep for backward compatibility, but we'll use accountIds when available
  accountIds?: string[]; // Array of account IDs that need consent refresh
  bankName?: string;
  bankBic?: string;
  bankRef?: string;
  bankCountryCode?: string;
  /** How each account needing renewal reads to the user, e.g. "Nordea ••7881" (FIN-909). */
  accountLabels?: string[];
  accountType?: AccountType;
}

const ConsentRefreshDialog = ({
  isOpen,
  onClose,
  onSuccess,
  accountId,
  accountIds,
  bankName: bankNameProp,
  bankBic,
  bankRef,
  bankCountryCode,
  accountLabels = [],
  accountType,
}: ConsentRefreshDialogProps) => {
  const t = useTranslations('consultantBank.renew');
  // The latest translator, so the polling closures and callbacks word their messages without being rebuilt.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const bankName = bankNameProp ?? t('yourBank');
  const identity = renewalIdentity(bankBic, bankRef, bankCountryCode);
  // A bank with a bic renews by it alone, as before FIN-885. `bic` is '' for a bic-less bank;
  // ref/country are then its whole identity on every call.
  const renewalBic = bankBic ?? '';
  const renewalRef = identity === 'ref' ? bankRef : undefined;
  const renewalCountry = identity === 'ref' ? bankCountryCode : undefined;
  const [refreshStep, setRefreshStep] = useState<'confirm' | 'consent' | 'complete'>('confirm');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycCompanyId, setKycCompanyId] = useState<string | null>(null);
  const [consentData, setConsentData] = useState<{
    consentId: string;
    authorisationId: string;
    scaRedirect?: string;
    consentStatus: string;
    scaStatus?: string;
    isCompleted?: boolean;
    isExisting?: boolean;
  } | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // multi-provider-banking F9 (#969 PR7 fix) — setInterval fires on a fixed 2s cadence
  // regardless of whether the PREVIOUS tick's async getConsentInitiationStatus call has
  // resolved yet; a slow tick (network latency, a real SCA/provider round trip) let two ticks'
  // callbacks run concurrently, and if BOTH observed isCompleted before either cleared the
  // interval, finishConsentRefresh (-> connectAccount) fired twice concurrently for the same
  // consent — confirmed live via ops-provider-invalidation.ts's own E2E test, where the second,
  // extraneous connectAccount call raced ahead of the first's own just-committed consent and
  // failed with "No valid consent found". Both refs are belt-and-suspenders: the in-flight
  // guard stops a new tick from starting while one is still running; the finished guard stops
  // finishConsentRefresh from running twice even if two ticks still overlap once each.
  const isPollTickInFlightRef = useRef(false);
  const hasFinishedRef = useRef(false);

  // No row is known to reach this (every connect stores a bic or a ref + country), so seeing it
  // means an account was stored without its identity -- reported, never guessed around (FIN-885).
  useEffect(() => {
    if (!isOpen || identity !== 'unrenewable') return;
    captureError(renewalRefusedError(bankRef), 'ConsentRefreshDialog: bank identity missing');
  }, [isOpen, identity, bankRef]);

  // Clean up polling on unmount or dialog close
  useEffect(() => {
    if (!isOpen) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Reset state when dialog closes
      setRefreshStep('confirm');
      setIsRefreshing(false);
      setError(null);
      setConsentData(null);
      isPollTickInFlightRef.current = false;
      hasFinishedRef.current = false;
    }
  }, [isOpen]);

  // Arrival timeout: not applicable (received=true) while the dialog is closed, before consent
  // data exists yet (i.e. before startConsentRefresh's initial call resolves), or once completed.
  const handleConsentPollTimeout = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsRefreshing(false);
    setError(tRef.current('timeout'));
    captureError(
      new Error(`Consent refresh polling: no completion signal for consentId=${consentData?.consentId} within ${ARRIVAL_TIMEOUT_MS}ms`),
      'ConsentRefreshDialog: arrival timeout'
    );
  }, [consentData?.consentId]);

  useArrivalTimeout(
    !isOpen || !consentData || consentData.isCompleted === true,
    ARRIVAL_TIMEOUT_MS,
    handleConsentPollTimeout
  );

  const startConsentRefresh = async () => {
    if (identity === 'unrenewable') return;

    setIsRefreshing(true);
    setError(null);
    setRefreshStep('consent');
    // A fresh refresh attempt (first click, or a retry after a prior one errored out) must be
    // able to call finishConsentRefresh again -- only a single already-in-progress session's
    // OWN overlapping poll ticks should ever be suppressed by hasFinishedRef.
    isPollTickInFlightRef.current = false;
    hasFinishedRef.current = false;

    try {
      const accountsToReconnect = accountIds && accountIds.length > 0 ? accountIds : [accountId];
      console.log("Starting consent refresh for account(s):", accountsToReconnect, "BIC:", renewalBic, "ref:", renewalRef, "country:", renewalCountry);

      // Always a fresh consent at the bank (FIN-863): the newest valid consent for this bank may be
      // the very one that no longer covers this account, and reusing it would loop.
      const consentService = ServiceRegistry.getConsentService();
      const consentInitiation = await consentService.initiateConsentCreation({ bic: renewalBic, ref: renewalRef, countryCode: renewalCountry, forceNew: true, accountType });

      console.log("Consent refresh initiated:", consentInitiation);
      setConsentData(consentInitiation);

      // If consent is already completed, skip polling and finish
      if (consentInitiation.isCompleted && !hasFinishedRef.current) {
        hasFinishedRef.current = true;
        console.log("Consent already completed, finishing refresh");
        setIsRefreshing(false);
        await finishConsentRefresh(consentInitiation.consentId);
        return;
      }
      
      // Start polling for consent completion
      startConsentPolling(consentInitiation.consentId, consentInitiation.authorisationId);
      
    } catch (error) {
      console.error("Error starting consent refresh:", error);

      // KYC required — company must complete identity verification before consent can be created
      if ((error as any)?.error === 'KYC_REQUIRED' || (error as any)?.message === 'KYC_REQUIRED') {
        const companyId = (error as any)?.details?.companyId;
        if (companyId) {
          setIsRefreshing(false);
          setRefreshStep('confirm');
          setKycCompanyId(companyId);
          setShowKycModal(true);
          return;
        }
      }

      setError(tRef.current('startFailed'));
      toast.error(tRef.current('startFailedToast'));
      setIsRefreshing(false);
      setRefreshStep('confirm');
    }
  };

  const startConsentPolling = (consentId: string, authorisationId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const thisInterval: NodeJS.Timeout = setInterval(async () => {
      if (pollingIntervalRef.current !== thisInterval) return; // superseded/cleared (e.g. arrival timeout)
      // Skip this tick entirely if the previous one's async call is still in flight -- a slow
      // response must never overlap with the next tick (see the ref's own comment above).
      if (isPollTickInFlightRef.current) return;
      isPollTickInFlightRef.current = true;
      try {
        const consentService = ServiceRegistry.getConsentService();
        const initiationStatus = await consentService.getConsentInitiationStatus(consentId, authorisationId, renewalBic, renewalRef, renewalCountry);

        console.log("Polling consent refresh status:", initiationStatus.consentStatus, "SCA status:", initiationStatus.scaStatus, "completed:", initiationStatus.isCompleted);

        // Update consent data with status info
        setConsentData(prev => prev ? {
          ...prev,
          consentStatus: initiationStatus.consentStatus,
          scaStatus: initiationStatus.scaStatus,
          isCompleted: initiationStatus.isCompleted
        } : null);

        // Declined or failed at the bank: terminal, so stop waiting rather than run out the
        // 3-minute arrival timeout. The account keeps its Access needed state.
        if (!initiationStatus.isCompleted && isTerminalConsentFailure(initiationStatus)) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setConsentData(null);
          setIsRefreshing(false);
          setRefreshStep('confirm');
          setError(tRef.current('declined'));
          return;
        }

        // Check if consent is completed
        if (initiationStatus.isCompleted && !hasFinishedRef.current) {
          hasFinishedRef.current = true;
          // Clear polling and finish
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          setIsRefreshing(false);
          console.log("Consent refresh completed, finishing up");

          await finishConsentRefresh(initiationStatus.consentId);
        }
      } catch (error) {
        console.error("Error polling consent refresh:", error);
        // Continue polling, don't fail on individual poll errors
      } finally {
        isPollTickInFlightRef.current = false;
      }
    }, 2000); // Poll every 2 seconds

    pollingIntervalRef.current = thisInterval;
  };

  const finishConsentRefresh = async (consentId?: string) => {
    try {
      console.log("Finishing consent refresh - connecting accounts under new consent");
      
      const activeConsentId = consentId || consentData?.consentId;
      if (!activeConsentId || identity === 'unrenewable') {
        console.error("Missing consent ID or bank identity for connecting accounts", JSON.stringify({activeConsentId, consentData, bankBic, bankRef}));
        return;
      }
      
      // Get all account IDs that need to be reconnected
      const accountsToReconnect = accountIds && accountIds.length > 0 ? accountIds : [accountId];
      console.log(`Connecting ${accountsToReconnect.length} existing account(s) to new consent:`, accountsToReconnect);
      
      // Connect each existing account to the new consent
      const accountService = ServiceRegistry.getAccountService();
      const connectionResults = [];
      
      for (const currentAccountId of accountsToReconnect) {
        try {
          console.log(`Connecting account ${currentAccountId} to new consent`);
          await accountService.connectAccount(currentAccountId, renewalBic, renewalRef, renewalCountry, accountType);
          console.log(`Successfully connected account ${currentAccountId} to new consent`);
          connectionResults.push({ accountId: currentAccountId, success: true });
        } catch (error) {
          console.error(`Failed to connect account ${currentAccountId}:`, error);
          connectionResults.push({ accountId: currentAccountId, success: false, error: userFacingErrorMessage(error, t('reconnectFailed')) });
          // Continue with other accounts even if one fails
        }
      }
      
      // Check if any connections failed
      const failedConnections = connectionResults.filter(r => !r.success);
      if (failedConnections.length > 0) {
        console.warn(`${failedConnections.length} account(s) failed to reconnect:`, failedConnections);
        // Still continue if at least one succeeded
        if (failedConnections.length === accountsToReconnect.length) {
          // Most often the fresh consent still doesn't cover the account (FIN-863): the renewal
          // went through, but the account wasn't selected at the bank.
          captureError(
            new Error(`Consent renewal for ${bankBic || bankRef}: none of ${accountsToReconnect.length} account(s) reconnected`),
            'ConsentRefreshDialog: no account reconnected after renewal'
          );
          setRefreshStep('confirm');
          setError(tRef.current('noneReconnected'));
          return;
        }
      }
      
      console.log(`Successfully connected ${connectionResults.filter(r => r.success).length}/${accountsToReconnect.length} accounts to new consent`);

      // Reload all bank accounts to get the updated data. A best-effort refresh, not the success
      // signal for the renewal itself: the relink above already succeeded, so a failed refresh
      // must not be reported as a failed renewal (multi-provider-banking F9).
      let updatedAccounts: BankAccountSummary[] | null = null;
      try {
        updatedAccounts = await accountService.getBankAccounts();
      } catch (refreshError) {
        console.warn("Reconnect succeeded but the post-reconnect account list refresh failed -- treating the reconnect itself as successful:", refreshError);
      }

      setRefreshStep('complete');

      // Wait a bit to show success, then close. Only hand fresh data up when the refresh
      // actually got some -- an empty/failed refresh must never overwrite the page's existing
      // account list with a wrong empty one; closing the dialog is enough, the page's own normal
      // data loading picks up the real (possibly still-partial) state on its next load.
      setTimeout(() => {
        if (updatedAccounts && updatedAccounts.length > 0) {
          onSuccess(updatedAccounts);
        }
        onClose();
        toast.success(tRef.current('refreshed'));
      }, 1500);
    } catch (error) {
      console.error("Error finishing consent refresh:", error);
      setError(tRef.current('loadAccountsFailed'));
      toast.error(tRef.current('loadAccountsFailedToast'));
    }
  };

  const renderConsentProgress = () => {
    if (isRefreshing && !consentData) {
      return (              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{t('creating')}</p>
              </div>
      );
    }
    if (!consentData) {
      return <p className="text-sm text-red-500 text-center">{t('createFailed')}</p>;
    }
    return (
              <div className="space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>{t('selectEveryAccount')}</AlertDescription>
                </Alert>
                {consentData.scaRedirect && !consentData.isCompleted && (
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() => window.open(consentData.scaRedirect, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('continueToBank')}
                  </Button>
                )}
                <div className="text-center">
                  {consentData.isCompleted ? (
                    <div className="space-y-2">
                      <CheckIcon className="h-8 w-8 text-green-600 mx-auto" />
                      <p className="text-sm font-medium text-green-600">{t('authorized')}</p>
                      <p className="text-xs text-muted-foreground">{t('refreshingAccess')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                      <p className="text-sm font-medium">{t('waitingForBank')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('approveRenewal')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
    );
  };

  const renderStepContent = () => {
    switch (refreshStep) {
      case 'confirm':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md border border-red-200 mb-4">
                {error}
              </div>
            )}
            
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <RefreshCw className="h-5 w-5 text-amber-600" />
                <span className="font-medium text-amber-900">{t('renewalRequired')}</span>
              </div>
              <p className="text-sm text-amber-800">
                {t.rich('expired', { bankName, strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
              {accountLabels.length > 0 && (
                <div className="mt-3 text-sm text-amber-900">
                  <p className="font-medium">{t('accessNeededFor')}</p>
                  <ul className="list-disc list-inside">
                    {accountLabels.map((label) => <li key={label}>{label}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {identity === 'unrenewable' && (
              <div role="alert" className="bg-red-50 text-red-600 p-3 rounded-md border border-red-200">
                {t('unavailable')}
              </div>
            )}

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium mb-3">{t('whatWillHappen')}</h4>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-500 mt-0.5" />
                  <p className="text-sm">{t('stepCreate')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <ExternalLink className="h-4 w-4 text-blue-500 mt-0.5" />
                  <p className="text-sm">{t('stepAuthorize')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckIcon className="h-4 w-4 text-green-500 mt-0.5" />
                  <p className="text-sm">{t('stepRestore')}</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={onClose}
                disabled={isRefreshing}
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={startConsentRefresh} 
                className="flex-1" 
                disabled={isRefreshing || identity === 'unrenewable'}
              >
                {isRefreshing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('starting')}
                  </>
                ) : (
                  t('renewAccess')
                )}
              </Button>
            </div>
          </div>
        );

      case 'consent':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md border border-red-200 mb-4">
                {error}
              </div>
            )}
            
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <RefreshCw className="h-5 w-5 text-primary" />
                <span className="font-semibold">{bankName}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('renewingConsent')}
              </p>
            </div>

            {renderConsentProgress()}

            {/* Back button */}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => {
                // Clear polling and go back
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
                setConsentData(null);
                setIsRefreshing(false);
                setRefreshStep('confirm');
              }}
              disabled={isRefreshing && consentData?.isCompleted}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('back')}
            </Button>
          </div>
        );

      case 'complete':
        return (
          <CompletedRedirectPanel
            title={t('completeTitle')}
            body={t('completeBody')}
            progressNote={t('updatingAccounts')}
          />
        );
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="glass-card max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-primary text-xl">
              {t(`steps.${refreshStep}.title`)}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {t(`steps.${refreshStep}.description`)}
            </DialogDescription>
          </DialogHeader>
          {renderStepContent()}
        </DialogContent>
      </Dialog>

      {showKycModal && kycCompanyId && (
        <KycModal
          open={showKycModal}
          onOpenChange={(open) => {
            setShowKycModal(open);
            if (!open) setKycCompanyId(null);
          }}
          companyId={kycCompanyId}
          provider="ztl"
          kycType="onboarding"
          onKycCompleted={() => {
            setShowKycModal(false);
            setKycCompanyId(null);
            // Retry consent refresh now that KYC is complete
            startConsentRefresh();
          }}
        />
      )}
    </>
  );
};

export default ConsentRefreshDialog;

/** The renewal dialog for one open renewal request, as both context providers render it. */
export function ConsentRenewalDialog({ target, onClose, onSuccess }: {
  target: ConsentRenewalTarget;
  onClose: () => void;
  onSuccess: (accounts: BankAccountSummary[]) => void;
}) {
  return (
    <ConsentRefreshDialog
      isOpen
      onClose={onClose}
      onSuccess={onSuccess}
      accountId={target.accountId}
      accountIds={target.accountIds}
      bankName={target.bankName}
      bankBic={target.bankBic}
      bankRef={target.bankRef}
      bankCountryCode={target.bankCountryCode}
      accountLabels={target.accountLabels}
      accountType={target.accountType}
    />
  );
}
